<script setup lang="ts">
import { ref, onMounted, computed } from 'vue'
import { useI18n } from 'vue-i18n'
import { useVenues } from '@/composables/useVenues'
import AppSpinner from '@/components/shared/AppSpinner.vue'
import ConfirmDialog from '@/components/shared/ConfirmDialog.vue'
import VenueFormFields from '@/components/organizer/venues/VenueFormFields.vue'
import type { VenueFormData } from '@/components/organizer/venues/VenueFormFields.vue'
import VenueEditSlideOver from '@/components/organizer/venues/VenueEditSlideOver.vue'
import { venueLabel as buildVenueLabel } from '@/utils/venueLabel'
import type { Venue, VenueDeletion } from '@/types/events'

const props = defineProps<{
  modelValue: number | null
  organisationId: number
  /**
   * Whether the "no selection" option is offered.
   *
   * REQUIRED (not optional with a default) so that both call sites must state
   * their intent: omitting it is a TypeScript error, which is what prevents a
   * silent regression on the edition form.
   *  - EditionForm passes false: the venue is mandatory, the empty option is removed.
   *  - RaceForm passes true: the empty option is retained and relabelled.
   */
  allowEmpty: boolean
  /** Key into `errors` and the select id suffix. Defaults to 'venue_id'. */
  fieldName?: string
  /** Label of the empty option. Defaults to t('editions.form.venue_none'). */
  emptyLabel?: string
  /** aria-label of the select. Defaults to t('editions.form.venue_label'). */
  ariaLabel?: string
  errors?: Record<string, string[]>
}>()

const emit = defineEmits<{
  'update:modelValue': [value: number | null]
  // User actions only (select change, inline create, delete); never emitted on mount or list fetch.
  'venue-selected': [venue: Venue | null]
  // Emitted after the venue was edited in the slide-over, with the saved venue.
  'venue-updated': [venue: Venue]
  deleted: [venueId: number]
}>()

const { t } = useI18n()

const {
  venues,
  loading,
  createErrors,
  deleting,
  deleteError,
  fetchVenues,
  createVenue,
  deleteVenue,
} = useVenues(props.organisationId)

const showCreateForm = ref(false)
const creating = ref(false)
const confirmDeleteOpen = ref(false)
const blockedHintCount = ref<number | null>(null)
const editPanelOpen = ref(false)

function emptyVenueForm(): VenueFormData {
  return { name: null, street: null, city: null, postal_code: null, country: null, timezone: '' }
}

const newVenue = ref<VenueFormData>(emptyVenueForm())

onMounted(fetchVenues)

const fieldKey = computed<string>(() => props.fieldName ?? 'venue_id')
const fieldErrors = computed<string[] | undefined>(() => props.errors?.[fieldKey.value])
const emptyOptionLabel = computed<string>(() => props.emptyLabel ?? t('editions.form.venue_none'))
const selectAriaLabel = computed<string>(() => props.ariaLabel ?? t('editions.form.venue_label'))

function venueLabel(venue: {
  name: string | null
  street: string | null
  city: string | null
  postal_code: string | null
  country: string | null
  latitude: string | null
  longitude: string | null
}): string {
  return buildVenueLabel(venue) ?? t('venues.unnamed')
}

const selectedVenue = computed<Venue | null>(() => {
  if (props.modelValue === null) return null
  return venues.value.find((venue) => venue.id === props.modelValue) ?? null
})

const selectedLabel = computed<string>(() => {
  const found = selectedVenue.value
  return found ? venueLabel(found) : emptyOptionLabel.value
})

/**
 * Deletability comes entirely from the API's `deletion` block. This component
 * never fetches editions, never compares dates, and never infers the verdict.
 *
 * The block is OPTIONAL in the contract: `VenueResource` omits it whenever the
 * edition-count aggregates were not loaded, precisely so that no consumer ever
 * receives a guessed verdict. A missing block therefore means UNKNOWN, not
 * "deletable" - falling back to "deletable" here would reintroduce the exact
 * false-verdict bug the backend fix removed, only client-side.
 */
const deletionVerdict = computed<VenueDeletion | null>(() => selectedVenue.value?.deletion ?? null)

/**
 * The delete affordance is offered only when the API actually stated a verdict.
 * No verdict, no delete button - not even a disabled one, because we cannot
 * honestly explain why it would be disabled.
 */
const canOfferDelete = computed<boolean>(() => deletionVerdict.value !== null)

const deletionBlocked = computed<boolean>(() => deletionVerdict.value?.status === 'blocked')

const confirmBody = computed<string>(() => {
  const verdict = deletionVerdict.value
  if (verdict === null) return ''
  if (verdict.status === 'deletable_with_warning') {
    return t('venues.delete.warning_body', { count: verdict.inactive_edition_count })
  }
  return t('venues.delete.confirm_body')
})

const blockedHint = computed<string | null>(() => {
  if (blockedHintCount.value !== null) {
    return t('venues.delete.blocked_hint', { count: blockedHintCount.value })
  }
  const verdict = deletionVerdict.value
  if (verdict !== null && verdict.status === 'blocked') {
    return t('venues.delete.blocked_hint', { count: verdict.active_edition_count })
  }
  return null
})

function handleSelectChange(event: Event): void {
  const target = event.target as HTMLSelectElement
  const val = target.value
  blockedHintCount.value = null
  const id = val === '' ? null : Number(val)
  emit('update:modelValue', id)
  emit('venue-selected', id === null ? null : (venues.value.find((venue) => venue.id === id) ?? null))
}

function openDeleteConfirm(): void {
  if (!canOfferDelete.value || deletionBlocked.value || selectedVenue.value === null) return
  blockedHintCount.value = null
  confirmDeleteOpen.value = true
}

function closeDeleteConfirm(): void {
  if (deleting.value) return
  confirmDeleteOpen.value = false
}

async function handleConfirmDelete(): Promise<void> {
  const venue = selectedVenue.value
  if (venue === null || !canOfferDelete.value) return

  const result = await deleteVenue(venue.id)
  confirmDeleteOpen.value = false

  if (result.ok) {
    emit('update:modelValue', null)
    emit('venue-selected', null)
    emit('deleted', venue.id)
    return
  }

  if (deleteError.value === null) {
    // The client verdict was stale: the API refused. Re-read the list so the
    // `deletion` block is fresh, and show the count the API just reported.
    blockedHintCount.value = result.activeEditionCount
    await fetchVenues()
  }
}

function toggleCreateForm(): void {
  showCreateForm.value = !showCreateForm.value
  if (showCreateForm.value) {
    newVenue.value = emptyVenueForm()
  }
}

function openEditPanel(): void {
  if (selectedVenue.value === null) return
  editPanelOpen.value = true
}

function closeEditPanel(): void {
  editPanelOpen.value = false
}

function handleVenueUpdated(venue: Venue): void {
  venues.value = venues.value.map((existing) => (existing.id === venue.id ? venue : existing))
  emit('venue-updated', venue)
}

async function handleCreateSubmit(): Promise<void> {
  creating.value = true
  const venue = await createVenue({ ...newVenue.value })
  creating.value = false
  if (venue) {
    emit('update:modelValue', venue.id)
    emit('venue-selected', venue)
    showCreateForm.value = false
    newVenue.value = emptyVenueForm()
  }
}

function handleCancelCreate(): void {
  showCreateForm.value = false
  newVenue.value = emptyVenueForm()
}
</script>

<template>
  <div class="space-y-3">
    <!-- Venue selector -->
    <div>
      <select
        :id="`venue-select-${fieldKey}`"
        :value="modelValue ?? ''"
        :disabled="loading"
        class="w-full rounded-lg bg-slate-800 border px-4 py-2.5 text-white text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
        :class="fieldErrors ? 'border-red-500' : 'border-slate-700 focus:border-emerald-500'"
        :aria-label="selectAriaLabel"
        @change="handleSelectChange"
      >
        <!-- Optional selection: the empty option is a real choice. -->
        <option v-if="allowEmpty" value="">{{ emptyOptionLabel }}</option>
        <!--
          Mandatory selection with nothing chosen yet: a disabled placeholder so
          the control is never blank and cannot be set back to empty.
        -->
        <option v-else-if="modelValue === null" value="" disabled>
          {{ t('editions.form.venue_required_placeholder') }}
        </option>
        <option
          v-for="venue in venues"
          :key="venue.id"
          :value="venue.id"
        >
          {{ venueLabel(venue) }}
        </option>
      </select>
      <p v-if="fieldErrors" class="mt-1 text-xs text-red-400">
        {{ fieldErrors[0] }}
      </p>
      <p v-if="loading && venues.length === 0" class="mt-1 text-xs text-slate-500">
        {{ t('common.loading') }}
      </p>
      <p
        v-if="!loading && venues.length === 0 && !showCreateForm"
        class="mt-1 text-xs text-slate-500"
      >
        {{ t('venues.empty_hint') }}
      </p>
    </div>

    <!-- Add new venue toggle -->
    <button
      v-if="!showCreateForm"
      type="button"
      class="text-sm text-emerald-400 hover:text-emerald-300 transition-colors font-medium"
      @click="toggleCreateForm"
    >
      + {{ t('editions.form.venue_add') }}
    </button>

    <!-- Inline create form -->
    <div
      v-if="showCreateForm"
      class="rounded-lg bg-slate-800/80 border border-slate-700 p-4 space-y-4"
    >
      <h4 class="text-sm font-semibold text-white">{{ t('editions.form.venue_add') }}</h4>

      <VenueFormFields
        v-model="newVenue"
        mode="create"
        :id-prefix="`venue-create-${fieldKey}`"
        :errors="createErrors"
      />

      <!-- Form actions -->
      <div class="flex items-center gap-2 pt-1">
        <button
          type="button"
          :disabled="creating || newVenue.timezone === ''"
          class="bg-emerald-500 hover:bg-emerald-400 disabled:opacity-50 disabled:cursor-not-allowed text-white font-semibold rounded-lg px-4 py-2 text-sm transition-colors flex items-center gap-2"
          @click="handleCreateSubmit"
        >
          <AppSpinner v-if="creating" size="sm" />
          {{ creating ? t('common.saving') : t('venues.create.save') }}
        </button>
        <button
          type="button"
          class="border border-slate-600 hover:border-slate-500 text-slate-300 hover:text-white font-semibold rounded-lg px-4 py-2 text-sm transition-colors"
          @click="handleCancelCreate"
        >
          {{ t('common.cancel') }}
        </button>
      </div>
    </div>

    <!-- Selected venue summary + delete affordance (when a venue is selected) -->
    <div
      v-if="modelValue !== null && !showCreateForm"
      class="space-y-1"
    >
      <div class="flex items-center justify-between gap-3">
        <span class="text-xs text-slate-500">
          {{ selectedLabel }}
          <template v-if="selectedVenue">
            <span class="text-slate-600"> - </span>
            <span data-testid="venue-selected-timezone">{{ t('venues.selected_timezone', { timezone: selectedVenue.timezone }) }}</span>
          </template>
        </span>
        <div class="flex items-center gap-3">
          <button
            v-if="selectedVenue"
            type="button"
            class="text-xs font-medium text-emerald-400 hover:text-emerald-300 transition-colors"
            data-testid="venue-edit-action"
            @click="openEditPanel"
          >
            {{ t('venues.edit.action') }}
          </button>
          <!--
            Rendered only when the API supplied a deletion verdict. A venue with
            no `deletion` block has an UNKNOWN verdict, so no delete affordance is
            offered at all.
          -->
          <button
            v-if="canOfferDelete"
            type="button"
            :disabled="deletionBlocked || deleting"
            class="text-xs font-medium text-red-400 hover:text-red-300 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            :aria-label="t('venues.delete.action')"
            data-testid="venue-delete-action"
            @click="openDeleteConfirm"
          >
            {{ t('venues.delete.action') }}
          </button>
        </div>
      </div>
      <p v-if="blockedHint" class="text-xs text-amber-400" data-testid="venue-delete-blocked-hint">
        {{ blockedHint }}
      </p>
      <p v-if="deleteError" class="text-xs text-red-400">
        {{ t('venues.delete.error') }}
      </p>
    </div>

    <VenueEditSlideOver
      :open="editPanelOpen"
      :organisation-id="organisationId"
      :venue-id="selectedVenue?.id ?? null"
      @close="closeEditPanel"
      @updated="handleVenueUpdated"
    />

    <!-- Destructive confirmation: shared centered dialog, per the UI patterns rules -->
    <ConfirmDialog
      :open="confirmDeleteOpen"
      :title="t('venues.delete.title')"
      :body="confirmBody"
      :confirm-label="t('venues.delete.action')"
      :loading="deleting"
      danger
      @update:open="closeDeleteConfirm"
      @confirm="handleConfirmDelete"
    />
  </div>
</template>
